import type React from 'react';
import { useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { upCaseFirstLetter } from '@polyai/common/lib/stringHelper';
import Button from '@polyai/ui/components/atoms/Button';
import LoadingIcon from '@polyai/ui/components/atoms/LoadingIcon';

import type { AssistantVoiceOption } from 'api/types';
import useAssistantConfigOptions from 'hooks/useAssistantConfigOptions';
import VoiceOptionButton from 'screens/AgentVoicesStream/components/VoiceOptionButton';
import type { Region } from 'screens/OpenTableRegion/types';
import FormProvider from '../../../components/molecules/Form';
import { AVAILABLE_REGIONAL_VOICES } from '../constants';
import { type AgentVoiceFormData, agentVoiceSchema } from '../types';

const NUMBER_OF_VOICES_TO_SHOW = 5;

interface AgentVoiceFormProps {
  onSubmit: (data: AgentVoiceFormData) => void;
  isSubmitting: boolean;
  region: Region;
}

const AgentVoiceForm: React.FC<AgentVoiceFormProps> = ({
  onSubmit,
  isSubmitting,
  region,
}) => {
  const available_voices = AVAILABLE_REGIONAL_VOICES[region]?.length
    ? AVAILABLE_REGIONAL_VOICES[region]
    : AVAILABLE_REGIONAL_VOICES['united-states'];

  const methods = useForm<AgentVoiceFormData>({
    resolver: zodResolver(agentVoiceSchema),
    defaultValues: {
      voice: undefined,
    },
    mode: 'onChange',
  });

  const { voiceOptions, isLoadingVoiceOptions } = useAssistantConfigOptions(
    true,
    true,
  );

  const selectVoice = methods.watch('voice');

  const voiceSelectOptions: AssistantVoiceOption[] = useMemo(() => {
    const selectedVoiceLanguageCode = (
      selectVoice as { languageCode?: string } | undefined
    )?.languageCode;
    return (
      available_voices
        ?.map(provider_voice_id => {
          const voice = voiceOptions?.find(
            v => v.provider_voice_id === provider_voice_id,
          );

          if (!voice) {
            return null;
          }

          const accent = voice.voice_metadata?.accent;
          return {
            ...voice,
            isTopVoice:
              accent !== undefined && accent === selectedVoiceLanguageCode,
          };
        })
        .filter(voice => voice !== null)
        .slice(0, NUMBER_OF_VOICES_TO_SHOW) ?? []
    );
  }, [voiceOptions, selectVoice, available_voices]);

  const { isValid } = methods.formState;

  return (
    <div className="flex flex-col gap-sm4">
      <h2 className="text-header-large font-semibold text-primary leading-tight">
        Select your agent&apos;s voice
      </h2>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <div className="flex flex-col gap-sm4">
          <div className="flex flex-col gap-xs2">
            {isLoadingVoiceOptions ? (
              <div className="flex items-center gap-xs2 font-semibold text-primary">
                <LoadingIcon />
                <div>Loading voices...</div>
              </div>
            ) : (
              voiceSelectOptions.map(voice => (
                <VoiceOptionButton
                  key={voice.id}
                  isSelected={selectVoice?.value === voice.id}
                  type="button"
                  voice={voice}
                  onClick={() =>
                    methods.setValue(
                      'voice',
                      {
                        label: upCaseFirstLetter(voice.name),
                        value: voice.id,
                      },
                      { shouldValidate: true, shouldDirty: true },
                    )
                  }
                />
              ))
            )}
          </div>
          <Button
            disabled={isSubmitting || !isValid}
            label="Create agent"
            type="submit"
            variant="PRIMARY"
            fullWidth
          />
        </div>
      </FormProvider>
    </div>
  );
};

export default AgentVoiceForm;
